// Libs
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTheme } from '@material-ui/core/styles';

// Components
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import Box from '@material-ui/core/Box';

// Actions
import { selectAudioSource } from 'REDUCERS/microphoneSlice';

// Utils
import useTranslator from 'UTILS/hooks/useTranslator';
import useFocus from 'UTILS/hooks/useFocus';
import { MICROPHONE_CHECK, DONE, RUNNING, AUDIO_METER_CANVAS_ID } from 'UTILS/constants';
import { stopStream } from 'UTILS/stream';
import { createVolumeMeter } from 'CHECKS/microphone/volumeMeter';
import translations from './locales.json';

const MicrophoneDiagnostic = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const microphoneCheck = useSelector(state => state.checks[MICROPHONE_CHECK]);
  const currentCheck = useSelector(state => state.nav.currentCheck);
  const [volumeMeter, setVolumeMeter] = useState(null);
  const [translate] = useTranslator(translations);

  // Show volume meter only if current check is the mic one, and only if it's done
  useEffect(() => {
    let shouldAbort = false;
    if (currentCheck === MICROPHONE_CHECK && microphoneCheck.stage === DONE) {
      const newVolumeMeter = createVolumeMeter({
        canvasId: AUDIO_METER_CANVAS_ID,
        color: theme.palette.primary.main,
      });
      setVolumeMeter(newVolumeMeter);
      navigator.mediaDevices.getUserMedia({
        audio: {
          deviceId: microphoneCheck.selectedVideoSourceId,
        },
      }).then((stream) => {
        if (!shouldAbort) {
          newVolumeMeter.start(stream);
        } else {
          stopStream(stream);
        }
      });
    } else if (volumeMeter) {
      volumeMeter.stop();
      setVolumeMeter(null);
    }
    return () => {
      shouldAbort = true;
    };
  }, [currentCheck, microphoneCheck.stage]);

  // Hide volume meter when the tab is blurred for performance concern
  let shouldAbort = false;
  useFocus(() => {
    if (currentCheck === MICROPHONE_CHECK && microphoneCheck.stage === DONE) {
      const newVolumeMeter = createVolumeMeter({
        canvasId: AUDIO_METER_CANVAS_ID,
        color: theme.palette.primary.main,
      });
      setVolumeMeter(newVolumeMeter);
      navigator.mediaDevices.getUserMedia({
        audio: {
          deviceId: microphoneCheck.selectedVideoSourceId,
        },
      }).then((stream) => {
        if (!shouldAbort) {
          newVolumeMeter.start(stream);
        } else {
          stopStream(stream);
        }
      });
    }
  }, () => {
    if (volumeMeter) {
      volumeMeter.stop();
    }
    setVolumeMeter(null);
    shouldAbort = true;
  }, [volumeMeter, currentCheck, microphoneCheck.stage]);


  const handleSourceChange = ({ target }) => {
    dispatch(selectAudioSource({ deviceId: target.value }));
    if (volumeMeter) {
      volumeMeter.stop();
      setVolumeMeter(null);
    }
  };

  return (
    <>
      {microphoneCheck.audioSourceList.length > 0 && (
        <Box className='microphoneDiagnostic-labelWrapper'>
          <FormControl variant='filled' fullWidth disabled={microphoneCheck.stage === RUNNING}>
            <InputLabel id='microphoneDiagnostic-selectLabel'>{translate('device')}</InputLabel>
            <Select
              labelId='microphoneDiagnostic-selectLabel'
              id='demo-simple-select-filled'
              value={microphoneCheck.selectedAudioSourceId}
              onChange={handleSourceChange}
            >
              {microphoneCheck.audioSourceList.map(source => (
                <MenuItem key={source.deviceId} value={source.deviceId}>
                  {source.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      )}
      {currentCheck === MICROPHONE_CHECK && microphoneCheck.stage === DONE && (
        <Box className='microphoneDiagnostic-audioMeterWrapper'>
          <canvas className='microphoneDiagnostic-audioMeter' id={AUDIO_METER_CANVAS_ID} />
        </Box>
      )}
    </>
  );
};

export default MicrophoneDiagnostic;
